import axios from 'axios'
import { axiosInstance } from '@methods/axiosConfig'
import { API_URL, GOCARDLESS_SERVICE_PATH } from '@constants/apiUrl'
import {
  getGocardlessContext,
  goToIncomeConfirmation,
  redirectBackFromGocardless,
} from '@methods/gocardless'
import type {
  ConfirmIbanRequest,
  ConfirmIbanResponse,
  GocardlessCountry,
  IbanSupplementContext,
  IbanSupplementErrorCode,
  PreviewIbanRequest,
  PreviewIbanResponse,
} from '@/types/gocardlessIbanSupplement'

// The IBAN screens (`/gocardless-iban-confirmation`) talk to the GoCardless web-api with the session
// cookie only, like the income confirmation screen; the session is handed back to onboarding through
// `redirect-back` at the end, which expires the cookie.

/** the URL param the country screen leaves for the confirmation screen (and for a reload of it) */
export const IBAN_COUNTRY_URL_PARAM = 'ibanCountry'
/**
 * the URL param the confirmation screen leaves when it sends the user back to the country screen
 * because the backend refused the IBAN: the `IbanSupplementErrorCode`, shown there as the reason
 */
export const IBAN_REFUSED_URL_PARAM = 'ibanRefused'

const LIST_COUNTRIES_URL = `${API_URL}/${GOCARDLESS_SERVICE_PATH}/list-countries`
const PREVIEW_IBAN_URL = `${API_URL}/${GOCARDLESS_SERVICE_PATH}/preview-iban`
const CONFIRM_IBAN_URL = `${API_URL}/${GOCARDLESS_SERVICE_PATH}/confirm-iban`
const ABORT_IBAN_URL = `${API_URL}/${GOCARDLESS_SERVICE_PATH}/abort-iban`

// The IBAN the country screen last previewed, for the first render of the confirmation screen: going
// on then costs one round trip and shows no second spinner. Memory only, keyed by the country and
// forgotten once the confirmation screen read it: a reload, an edited URL or a screen mounted again
// through the browser's history asks the backend again.
let lastPreview: { countryCode: string; iban: string } | undefined

export const getIbanSupplementContext = async (): Promise<IbanSupplementContext | undefined> =>
  (await getGocardlessContext())?.ibanSupplement

export const listGocardlessCountries = async (): Promise<GocardlessCountry[]> => {
  const { data } = await axiosInstance.get<{ countries: GocardlessCountry[] }>(LIST_COUNTRIES_URL, {
    withCredentials: true,
  })

  return data?.countries ?? []
}

export const previewIban = async (countryCode: string): Promise<PreviewIbanResponse> => {
  const body: PreviewIbanRequest = { countryCode }
  const { data } = await axiosInstance.post<PreviewIbanResponse>(PREVIEW_IBAN_URL, body, {
    withCredentials: true,
  })

  lastPreview = { countryCode, iban: data.iban }
  return data
}

/** the IBAN `previewIban` last got for this country, if any; see `forgetPreviewedIban` */
export const previewedIbanFor = (countryCode: string): string | undefined =>
  lastPreview?.countryCode === countryCode ? lastPreview.iban : undefined

/** the confirmation screen read the preview: the next one mounted asks the backend */
export const forgetPreviewedIban = (): void => {
  lastPreview = undefined
}

export const confirmIban = async (
  countryCode: string,
  iban: string
): Promise<ConfirmIbanResponse> => {
  const body: ConfirmIbanRequest = { countryCode, iban }
  const { data } = await axiosInstance.post<ConfirmIbanResponse>(CONFIRM_IBAN_URL, body, {
    withCredentials: true,
  })

  // answered: a confirmation screen mounted again (Back) asks the backend where the session is
  lastPreview = undefined
  return data
}

export const abortIbanSupplement = async (): Promise<void> => {
  await axiosInstance.post(ABORT_IBAN_URL, {}, { withCredentials: true })
}

/**
 * Leaves the screens when there is nothing to ask: the session was never asked (no slice, e.g. a flow
 * without the feature), or it already answered (a reload after confirming). Returns whether it did.
 */
export const leaveWhenNothingToAsk = (context: IbanSupplementContext | undefined): boolean => {
  if (!context) {
    redirectBackFromGocardless()
    return true
  }

  if (context.status === 'confirmed') {
    if (context.next === 'incomeConfirmation') {
      goToIncomeConfirmation()
    } else {
      redirectBackFromGocardless()
    }
    return true
  }

  return false
}

const hasStatus = (error: unknown, status: number): boolean =>
  axios.isAxiosError(error) && error.response?.status === status

/**
 * The IBAN endpoints refused the request for a reason of their own, see `errorCode`: a 400, or the 404
 * of a session without an account number. Anything else (the network, a 5xx) may succeed on a retry.
 */
export const wasRefused = (error: unknown): boolean =>
  hasStatus(error, 400) || hasStatus(error, 404)

/** the `error` code a refused request of the IBAN endpoints carries */
export const errorCode = (error: unknown): IbanSupplementErrorCode | undefined =>
  axios.isAxiosError(error)
    ? (error.response?.data as { error?: IbanSupplementErrorCode } | undefined)?.error
    : undefined
